import appInfo from "@common/appInfo";
import CancelButton from "@common/controls/Form/Buttons/Cancel";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormField } from "@common/controls/Form/component";
import { type DynamicForms } from "@common/controls/Form/DynamicForm/types";
import { FancyCheckbox } from "@common/controls/Inputs/Checkbox/component";
import Select from "@common/controls/Inputs/Select/component";
import { FormModalControlled, type FormModalControlledRenderParams } from "@common/controls/Modal/Modals/Form/component";
import { type GenericMap, type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { isCryptoCurrency } from "@common/lib/currency";
import { usePromise } from "@common/lib/hooks";
import { type CurrencyOptionItem } from "@common/lib/options";
import { type Account } from "@common/modules/account/types";
import * as React from "react";
import { type IRecipientFields, type TransferApi } from "../types";
import BankFields from "./BankFields";
import CryptoFields from "./CryptoFields";
import WalletFields from "./WalletFields";

export type DynamicData = {
  fields: DynamicField[];
  quoteId: string;
};

export type DynamicField = DynamicForms.Field & { refreshRequirementsOnChange: boolean };

export type AddEditRecipientGetDynamicFields = (
  data: Account.Api.GetBeneficiaryDynamicDataRequest
) => Promise<DynamicData>;

export type AddEditRecipientProps = {
  api: TransferApi;
  defaultValues: Partial<Account.View.BeneficiarySettings>;
  isSubmitting?: boolean;
  isVisible: boolean;
  onClose: () => void;
  previewStyle?: React.CSSProperties;
  submit: (data: Account.View.BeneficiarySettings) => Promise<any>;
  width?: string | number;
};

export type AddUpdateRecipientFormData = {
  hasAccepted: boolean;
  currencyCode: string | null;
  name: string | null;
  recipientType: RecipientType;

  // bank
  accountType: string | null;
  branchCode: string | null;
  fieldValues: GenericMap<any> | null;

  // crypto
  blockchainNetwork: string | null;
  walletAddress: string | null;

  // wallet to wallet
  accountNumber: string | null;
};

type RecipientType = "bank" | "crypto" | "wallet";

export const AddEditRecipientModal = (props: AddEditRecipientProps) => {
  const i18n = useI18n();

  const [isWalletTransferEnabled, isWalletTransferEnabledLoading] = usePromise<boolean>({
    fn: props.api.getIsWalletTransferEnabled,
    args: [],
    shouldLoad: props.isVisible
  });

  const [currencyOptions, isCurrencyOptionsLoading] = usePromise<CurrencyOptionItem[]>({
    fn: props.api.getCurrencies,
    args: [],
    shouldLoad: props.isVisible
  });

  const defaultValues = React.useMemo(
    (): Partial<AddUpdateRecipientFormData> => ({
      ...props.defaultValues,
      accountType: props.defaultValues.data?.wise?.additionalData?.accountType,
      branchCode: props.defaultValues.data?.wise?.additionalData?.branchCode,
      recipientType: props.defaultValues.data?.wise
        ? "bank"
        : props.defaultValues.data?.crypto
        ? "crypto"
        : props.defaultValues.data?.wallet
        ? "wallet"
        : undefined
    }),
    [props.defaultValues]
  );

  const submit = React.useCallback(
    async (data: AddUpdateRecipientFormData) => {
      if (!fieldsRef.current) throw new Error("BUG");

      const values = await fieldsRef.current.getValues(data);
      const request = {
        ...values,
        userId: props.defaultValues.userId,
        beneficiaryId: props.defaultValues.beneficiaryId
      };
      return props.submit(request);
    },
    [props.submit, props.defaultValues.beneficiaryId, props.defaultValues.userId]
  );

  const recipientTypeOptions = React.useMemo((): OptionItem<RecipientType>[] => {
    const options: OptionItem<RecipientType>[] = [];
    if (currencyOptions?.some((x) => !isCryptoCurrency(x.value))) {
      options.push({ label: i18n.bank, value: "bank" });
    }
    if (currencyOptions?.some((x) => isCryptoCurrency(x.value))) {
      options.push({ label: i18n.crypto, value: "crypto" });
    }
    if (appInfo.installedApps.includes("Wallets") && isWalletTransferEnabled) {
      options.push({ label: i18n.wallet, value: "wallet" });
    }
    return options;
  }, [i18n, currencyOptions, isWalletTransferEnabled]);

  const cryptoCurrencyOptions = React.useMemo(
    () => (currencyOptions ?? []).filter((x) => isCryptoCurrency(x.value)),
    [currencyOptions]
  );

  const fiatCurrencyOptions = React.useMemo(
    () => (currencyOptions ?? []).filter((x) => !isCryptoCurrency(x.value)),
    [currencyOptions]
  );

  const fieldsRef = React.useRef<IRecipientFields>(null);

  return (
    <FormModalControlled
      defaultValues={defaultValues}
      isVisible={props.isVisible}
      onClose={props.onClose}
      submit={submit}
      title={props.defaultValues.beneficiaryId ? i18n.updateRecipient : i18n.newRecipient}
      width={props.width}
    >
      {({ onChange, values }: FormModalControlledRenderParams<AddUpdateRecipientFormData>) => (
        <>
          <FormField
            label={i18n.accountType}
            Component={Select}
            componentProps={{
              isLoading: isCurrencyOptionsLoading || isWalletTransferEnabledLoading,
              options: recipientTypeOptions
            }}
            isRequired
            name="recipientType"
            value={values.recipientType}
            qa="Account type"
            onChange={(name, value) => {
              onChange("recipientType", value);

              // reset the whole thing
              onChange("currencyCode", null);
              onChange("fieldValues", null);
              onChange("accountType", null);
              onChange("blockchainNetwork", null);
              onChange("walletAddress", null);
            }}
          />

          {!!values.recipientType && (
            <>
              {values.recipientType === "wallet" ? (
                <WalletFields
                  ref={fieldsRef}
                  lookupAccount={props.api.lookupAccount}
                  onChange={onChange}
                  previewStyle={props.previewStyle}
                  values={values}
                />
              ) : (
                <>
                  <FormField
                    label={i18n.currency}
                    Component={Select}
                    componentProps={{
                      options: values.recipientType === "crypto" ? cryptoCurrencyOptions : fiatCurrencyOptions,
                      isLoading: isCurrencyOptionsLoading
                    }}
                    isRequired
                    name="currencyCode"
                    value={values.currencyCode}
                    qa="Currency"
                    onChange={(n, v) => {
                      onChange("currencyCode", v);
                      onChange("fieldValues", null);

                      if (values.recipientType === "crypto") {
                        onChange(
                          "blockchainNetwork",
                          cryptoCurrencyOptions!.find((x) => x.value === v)!.blockchainNetworkKey
                        );
                      }
                    }}
                  />

                  {!!values.currencyCode && (
                    <>
                      {values.recipientType === "bank" && (
                        <BankFields
                          ref={fieldsRef}
                          beneficiaryId={props.defaultValues.beneficiaryId}
                          getDynamicFields={props.api.recipient.getDynamicData}
                          onChange={onChange}
                          values={values}
                        />
                      )}

                      {values.recipientType === "crypto" && (
                        <CryptoFields
                          ref={fieldsRef}
                          getBlockchainNetworks={props.api.getBlockchainNetworks}
                          onChange={onChange}
                          values={values}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              <FormField
                Component={FancyCheckbox}
                componentProps={{ label: i18n.infoRecipientTransferDetailsWarning }}
                isRequired
                label={null}
                name="hasAccepted"
                value={values.hasAccepted}
                validator={(x) => (x ? null : i18n.errorRequiredField)}
              />
            </>
          )}

          <FormButtons>
            <CancelButton />
            <SubmitButton />
          </FormButtons>
        </>
      )}
    </FormModalControlled>
  );
};
